import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import API_BASE_URL from '../config';
import { getSessionId } from '../utils/session';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [availableRoles, setAvailableRoles] = useState(['buyer']);
  const [isUserLoaded, setIsUserLoaded] = useState(false);

  const fetchRoles = useCallback(async (authToken) => {
    try {
      const tokenToUse = authToken || localStorage.getItem('token');
      if (!tokenToUse) return ['buyer'];
      
      const rolesRes = await axios.get(`${API_BASE_URL}/api/user/roles`, {
        headers: { 'Authorization': `Bearer ${tokenToUse}` }
      });
      const roles = rolesRes.data.roles || ['buyer'];
      setAvailableRoles(roles);
      return roles;
    } catch (error) {
      console.error('Failed to fetch roles:', error);
      return ['buyer'];
    }
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      localStorage.removeItem('curious_email');
      localStorage.removeItem('curious_name');
      localStorage.removeItem('ourpickup_session');
      const response = await axios.get(`${API_BASE_URL}/api/auth/me`);
      const userData = response.data;
      
      console.log('📡 User data from /me:', userData.email, 'role:', userData.role);
      
      setUser(userData);
      setIsUserLoaded(true);
      // Fetch available roles
      const roles = await fetchRoles();
      setAvailableRoles(roles);
      
    } catch (error) {
      console.error('Failed to fetch user:', error);
      setIsUserLoaded(true);
      if (error.response && error.response.status === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  }, [fetchRoles]);

  useEffect(() => {
    console.log('AuthProvider useEffect, token:', !!token);
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      console.log('No token, setting loading false');
      setLoading(false);
    }
  }, [token, fetchUser]);

const login = async (email, password, twoFactorToken = null) => {
  console.log('📡 Login attempt for:', email);
  try {
    localStorage.removeItem('curious_email');
    localStorage.removeItem('curious_name');
    localStorage.removeItem('ourpickup_session');
    
    const response = await axios.post(`${API_BASE_URL}/api/auth/login`, { 
      email, 
      password,
      twoFactorToken: twoFactorToken || undefined
    });
    
    const { token, user } = response.data;
    console.log('✅ Login successful for:', user.email);

    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setToken(token);

    const roles = await fetchRoles(token);
    console.log('📋 Roles available:', roles);

    setUser(user);
    setIsUserLoaded(true);
    setAvailableRoles(roles);

    toast.success(`Logged in as ${user.role.replace(/_/g, ' ')}`);
    return true;
  } catch (error) {
    console.error('❌ Login failed:', error);
    
    // ✅ Re-throw so the Login page can handle 2FA
    throw error;
  }
};

const register = async (userData) => {
  console.log('📡 Register attempt for:', userData.email);
  try {
    localStorage.removeItem('curious_email');
    localStorage.removeItem('curious_name');
    localStorage.removeItem('ourpickup_session');
    const sessionId = localStorage.getItem('ourpickup_session');
    const headers = {
      'Content-Type': 'application/json'
    };
    if (sessionId) {
      headers['X-Session-Id'] = sessionId;
      console.log('📤 Sending session ID with registration:', sessionId);
    }

    const response = await axios.post(`${API_BASE_URL}/api/auth/register`, userData, { headers });
    const { token, user } = response.data;
    
    // If token is returned (pre-verified user), set it
    if (token) {
      console.log('✅ Token received from registration, auto-logging in');
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setToken(token);
      setUser(user);
      
      const roles = await fetchRoles(token);
      setAvailableRoles(roles);
      setIsUserLoaded(true);
      
      toast.success('Registered successfully!');
    } else {
      // Normal registration flow - need to verify email
      toast.success('Registered successfully! Please verify your email.');
    }
    
    // Return the full response data
    return response.data;
  } catch (error) {
    console.error('❌ Registration failed:', error);
    toast.error(error.response?.data?.message || 'Registration failed');
    return false;
  }
};

const logout = async () => {
  console.log('🔓 Logging out');
  try {
    const token = localStorage.getItem('token');
    if (token) {
      await axios.post(`${API_BASE_URL}/api/auth/logout`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      console.log('✅ Token blacklisted on server');
    }
  } catch (error) {
    // If the server is down or returns error, still logout locally
    console.error('Logout API error:', error.response?.data || error.message);
  } finally {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setAvailableRoles(['buyer']);
    setLoading(false);
    toast.success('Logged out successfully!');
  }
};

const switchRole = async (newRole) => {
  console.log('🔄 Switching role to:', newRole);
  try {
    // Update the user object
    setUser(prevUser => ({
      ...prevUser,
      role: newRole
    }));

    // Store the selected role preference
    localStorage.setItem('selectedRole', newRole);

    toast.success(`Switched to ${newRole.replace(/_/g, ' ')} role`);
  } catch (error) {
    console.error('Error switching role:', error);
    toast.error('Failed to switch role');
  }
};

  // Update user without full logout
  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    // Refresh roles
    fetchRoles();
  };

  const value = {
    user,
    loading,
    isUserLoaded,
    token,
    login,
    register,
    logout,
    switchRole,
    updateUser,
    fetchRoles,
    setUser,
    availableRoles
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
